//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import {
  InfiniteData,
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import {
  CAMPAIGN_CREATORS_LIST_PAGE_SIZE,
  DEFAULT_GALLERY_PAGE_SIZE,
  DEFAULT_PAGE_NUMBER,
} from '@apis/campaign-requests/shared/Pagination';
import { getContentLibraryItems } from '@apis/content-library/contentLibraryApi';
import {
  IContentLibraryItem,
  IContentLibraryItemParams,
  IContentLibraryItemsResponse,
  IDeserializedContentLibraryItem,
  IDeserializedContentLibraryItemsResponse,
} from '@apis/content-library/contentLibraryApiModel';
import { searchCreatorsCrossPlatform } from '@apis/creator-details/creatorDetailsApi';
import { CrossPlatformCreatorsLookupResponse } from '@apis/creator-details/creatorDetailsApiModel';
import {
  addCreatorNotes,
  deleteCreatorNote,
  editCreatorNote,
  getCreatorNotes,
} from '@apis/creator-notes/CreatorNotesApi';
import { CreatorNotesResponse } from '@apis/creator-notes/CreatorNotesApiModel';
import {
  addCreatorsToCreatorsCampaigns,
  applySparkCode,
  createCreatorsClientCampaign,
  deleteCreators,
  deleteCreatorsCampaign,
  deleteCreatorsCampaignContent,
  deletePendingCreators,
  disableCreatorsCampaignSensitiveLinksWarning,
  duplicateCreatorsCampaign,
  exportCreatorContentApprovalLinksCSV,
  exportCreatorPortalLinkCSV,
  getCampaignSparkCode,
  getConnectedImpactPartners,
  getCreatorContentApprovalLink,
  getCreatorPortalLink,
  getCreatorPortalLinks,
  getCreatorsCampaignWorkflowProducts,
  getCreatorsCampaignsAffiliateOrdersDetails,
  getCreatorsCampaignsAffiliatesAggregateStats,
  getCreatorsCampaignsAffiliatesGAConfigurationList,
  getCreatorsCampaignsAffiliatesList,
  getCreatorsCampaignsInfo,
  getCreatorsCampaignsList,
  getCreatorsCampaignsMembersList,
  getCreatorsCampaignsMetrics,
  getCreatorsCampaignsProposalsDetailsCSV,
  getCreatorsCampaignsProposalsList,
  getCreatorsCampaignsReport,
  getCreatorsCampaignsWorkflow,
  getCreatorsCampaignsWorkflowPreviewToken,
  getLinkedImpactProgram,
  getPartnerMappings,
  getSparkCodeMediaIds,
  getTiktokAdAccounts,
  linkImpactProgramToCampaign,
  linkTiktokVideoToTTOCampaign,
  mapCreatorsToImpactPartners,
  postRequestSparkCodeAuthorization,
  unlinkCreatorFromImpactPartner,
  updateCreatorsCampaignsCreatorMetadata,
  updateCreatorsCampaignsWorkflow,
  updateCreatorsClientCampaign,
  uploadCreatorMedia,
} from '@apis/creators-campaigns/CreatorsCampaignsApi';
import {
  ActiveCreatorsCampaignsListRequest,
  AffiliateType,
  CampaignWorkflowProductsListRequest,
  CreatorsCampaignWorkflowStep,
  CreatorsCampaignsAffiliateOrdersRequest,
  CreatorsCampaignsAffiliatesAggregateStatsResponse,
  CreatorsCampaignsAffiliatesGAConfigurationRequest,
  CreatorsCampaignsAffiliatesGoogleAnalyticsConfigurationResponse,
  CreatorsCampaignsAffiliatesListRequest,
  CreatorsCampaignsInfoResponse,
  CreatorsCampaignsMemberInfo,
  CreatorsCampaignsMembersListResponse,
  CreatorsCampaignsProposalsListResponse,
  CreatorsCampaignsReportQueryResponse,
  CreatorsCampaignsUpdateCreatorMetadataRequest,
  CreatorsCampaignsWorkflowResponse,
  CreatorsCampaignsWorkflowUpdateRequest,
  DuplicateCreatorsCampaignRequest,
  GetCampaignSparkCodeRequest,
  GetConnectedImpactPartnersRequest,
  GetCreatorsCampaignAffiliatesStatsRequest,
  GetPartnerMappingsRequest,
  GetSparkCodeMediaIdsRequest,
  ImpactMappingRequest,
  LinkTiktokVideoToTTOCampaignRequest,
  UploadCreatorMediaRequest,
  UpsertCreatorsCampaignRequest,
} from '@apis/creators-campaigns/CreatorsCampaignsApiModel';
import {
  CreatorsCampaignsCreateOrderRequest,
  CreatorsCampaignsOrderDetailsRequest,
  CreatorsCampaignsOrdersListQueryRequest,
  CreatorsCampaignsOrdersListResponse,
} from '@apis/creators-campaigns/creators-orders/CreatorOrdersApiModel';
import {
  createCreatorsCampaignsOrder,
  getCreatorsCampaignsOrderDetails,
  getCreatorsCampaignsOrdersList,
} from '@apis/creators-campaigns/creators-orders/CreatorsOrdersApi';
import {
  getCreatorsCampaignsProposalDetails,
  getCreatorsCampaignsProposalOrderEligibleProducts,
  updateCreatorsCampaignsProposalStatusWithAffiliates,
} from '@apis/creators-campaigns/creators-proposals/CreatorsProposalsApi';
import {
  CreatorsCampaignsProposalDetailsRequest,
  CreatorsCampaignsProposalDetailsResponse,
  CreatorsCampaignsProposalStatusRequestBase,
} from '@apis/creators-campaigns/creators-proposals/CreatorsProposalsApiModel';
import {
  LinkImpactProgramRequest,
  UnlinkCreatorFromImpactPartnerRequest,
} from '@apis/impact/ImpactApiModel';
import {
  getCampaignAnalyticsStats,
  getMetadata,
} from '@apis/metrics/metricsApi';
import { CampaignAnalyticsStatsResponse } from '@apis/metrics/metricsApiModel';
import { getShopifyPricingRules } from '@apis/shopify/ShopifyApi';
import { ShopifyPricingRuleRequest } from '@apis/shopify/ShopifyApiModel';
import { getCurrentTrackingParams } from '@apis/universal-image-actions/universalImageActionsApi';
import { DEFAULT_ALL_SELECTION } from '@campaigns/components/campaign-requests/creator-campaigns/CreatorsCampaignDetailsConstants';
import {
  BasicCreatorsInfo,
  CampaignAnalyticsStatsRequest,
  CreatorsCampaignsProposalsListsQueryRequest,
} from '@campaigns/components/campaign-requests/creator-campaigns/CreatorsCampaignsTypes';
import { getNetworkListFromPlatformFilter } from '@campaigns/components/campaign-requests/creator-campaigns/shared/utils/creatorsCampaignsUtils';
import { CAMPAIGNS_UNIFIED_METRICS_STATS_EARLIEST_DATE } from '@campaigns/constants/UnifiedMetricsStatsConstants';
import { parseFullName } from '@campaigns/utils/FormUtils';
import {
  getValidCampaignEndDate,
  hasCreatorCampaignsGAEnabled,
} from '@campaigns/utils/creators/CreatorCampaignUtils';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import { SocialCreatorsPlatforms } from '@models/CreatorsTypes';
import Interval from '@models/Interval';
import ItemId from '@models/ItemId';
import PartialCuid from '@models/PartialCuid';
import { SocialNetwork } from '@models/SocialNetwork';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import { FIVE_MINUTES_STALE_TIME } from '@queries/QueryOptionsConstants';
import { navigateToState } from '@react-bridge/wrappers/navigation';
import { isDefined, isEmptyValue, isUndefined } from '@utils/filterPredicates';
import { getRequestErrorCode } from '@utils/getRequestErrorCode';
import { getRequestErrorMessage } from '@utils/getRequestErrorMessage';
import { UNKNOWN_CURRENCY } from '@utils/readableNumber';
import { RequestError } from '@utils/sessionFetch';

const SEARCH_INFLUENCERS_LIMIT = 20;
const CAMPAIGN_REPORT_NETWORKS: SocialNetwork[] = [
  SocialNetwork.Instagram,
  SocialNetwork.YouTube,
];

export function useCreatorsCampaignsInfoQuery<
  T = CreatorsCampaignsInfoResponse,
>({
  campaignSlugName,
  select,
}: {
  campaignSlugName: string | undefined;
  select?: (data: CreatorsCampaignsInfoResponse) => T;
}) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CreatorsCampaigns,
      QueryKeyConstants.CreatorsCampaignsInformation,
      campaignSlugName,
    ],
    select,
    queryFn: async ({ signal }) => {
      if (!isDefined(campaignSlugName)) {
        throw new Error('Undefined CampaignSlugName');
      }
      const queryResults = await getCreatorsCampaignsInfo(
        { campaignSlugName },
        { signal },
      );
      // TODO: PD-283888: replace default currency on frontend with currency sourced from services on gateway
      return { campaignCurrency: 'USD', ...queryResults };
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
}

export function useCreatorsCampaignsWorkflowQuery<
  T = CreatorsCampaignsWorkflowResponse,
>({
  campaignSlugName,
  isEnabled = true,
  published,
  select,
}: {
  campaignSlugName: string | undefined;
  published?: boolean;
  select?: (data: CreatorsCampaignsWorkflowResponse) => T;
  isEnabled?: boolean;
}) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CreatorsCampaigns,
      QueryKeyConstants.CreatorsCampaignWorkflow,
      campaignSlugName,
      ...(published ? [{ published }] : []),
    ],
    select,
    queryFn: async ({ signal }) => {
      if (!isDefined(campaignSlugName)) {
        throw new Error('Undefined CampaignSlugName');
      }
      try {
        const queryResults = await getCreatorsCampaignsWorkflow(
          { campaignSlugName, ...(published ? { published } : {}) },
          { signal },
        );
        // TODO: PD-283888: replace default currency on frontend with currency sourced from services on gateway
        return { campaignCurrency: 'USD', ...queryResults };
      } catch (err) {
        const code = getRequestErrorCode(err);
        if (code === 404) {
          // Currently Affable backend returns a 404 error if campaign workflow does not exist.
          // It is okay for us to return an empty workflow in this case.
          return {};
        }

        throw err;
      }
    },
    enabled: isEnabled,
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
}

export function useCreatorsCampaignWorkflowProductsListQuery(
  req: CampaignWorkflowProductsListRequest,
) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CreatorsCampaigns,
      QueryKeyConstants.CreatorsCampaignWorkflow,
      req.campaignSlugName,
      QueryKeyConstants.CreatorsCampaignWorkflowProducts,
      req,
    ],
    queryFn: async ({ signal }) => {
      return getCreatorsCampaignWorkflowProducts(req, { signal });
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
}

export function useUpdateCreatorsCampaignWorkflowMutation(mutateOptions?: {
  onMutateHandler: (
    variables: CreatorsCampaignsWorkflowUpdateRequest,
  ) => Promise<Record<string, any>>;
  onErrorHandler: (
    err: unknown,
    variables: CreatorsCampaignsWorkflowUpdateRequest,
    context: Record<string, any> | undefined,
  ) => unknown;
}) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const queryKey = [
    QueryKeyConstants.Campaigns,
    QueryKeyConstants.CreatorsCampaigns,
    QueryKeyConstants.CreatorsCampaignWorkflow,
  ];

  return useMutation({
    mutationFn: (payload: CreatorsCampaignsWorkflowUpdateRequest) => {
      return updateCreatorsCampaignsWorkflow(payload);
    },
    onMutate: async ({
      step,
      ...variables
    }: CreatorsCampaignsWorkflowUpdateRequest) => {
      // this function is used for the optimistic update pattern
      const workflowQueryKey = [
        ...queryKey,
        variables.campaignSlugName,
        ...(variables.shouldSaveRevision ? [{ published: true }] : []),
      ];
      await queryClient.cancelQueries({
        queryKey: workflowQueryKey,
      });
      const previousCampaignWorkflow =
        queryClient.getQueryData<CreatorsCampaignsWorkflowResponse>(
          workflowQueryKey,
        );
      queryClient.setQueryData<CreatorsCampaignsWorkflowResponse>(
        workflowQueryKey,
        (prevData) => {
          const newData = { ...prevData };
          switch (step) {
            case CreatorsCampaignWorkflowStep.Basics:
              newData.campaignBrief = variables.workflow.campaignBrief;
              newData.campaignImageUrl = variables.workflow.campaignImageUrl;
              break;
            case CreatorsCampaignWorkflowStep.ContentRights:
              newData.contentRights = variables.workflow.contentRights;
              break;
            case CreatorsCampaignWorkflowStep.Payment:
              newData.fixedPay = variables.workflow.fixedPay;
              newData.storeConfigurations =
                variables.workflow.storeConfigurations;
              newData.influencersCommission =
                variables.workflow.influencersCommission;
              newData.googleAnalyticsConfiguration =
                variables.workflow.googleAnalyticsConfiguration;
              newData.isCollectPaymentDetailsEnabled =
                variables.workflow.isCollectPaymentDetailsEnabled;
              break;
            case CreatorsCampaignWorkflowStep.Tasks:
              newData.socoInfluencerTasks =
                variables.workflow.socoInfluencerTasks;
              newData.contentReviewEnabled =
                variables.workflow.contentReviewEnabled;
              break;
            case CreatorsCampaignWorkflowStep.Products:
              newData.socoProductGift = variables.workflow.socoProductGift;
              break;
            default:
              break;
          }

          return newData;
        },
      );

      return {
        previousCampaignWorkflow,
        ...(isDefined(mutateOptions?.onMutateHandler)
          ? await mutateOptions.onMutateHandler({
              step,
              ...variables,
            })
          : {}),
      };
    },
    onError: (
      err,
      variables: CreatorsCampaignsWorkflowUpdateRequest,
      context:
        | {
            previousCampaignWorkflow:
              | CreatorsCampaignsWorkflowResponse
              | undefined;
          }
        | undefined,
    ) => {
      if (isDefined(mutateOptions?.onErrorHandler)) {
        mutateOptions.onErrorHandler(err, variables, context);
      }
      if (context?.previousCampaignWorkflow) {
        // rollback for optimistic update pattern
        queryClient.setQueryData<CreatorsCampaignsWorkflowResponse>(
          [
            ...queryKey,
            variables.campaignSlugName,
            ...(variables.shouldSaveRevision ? [{ published: true }] : []),
          ],
          context.previousCampaignWorkflow,
        );
      }
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Campaign Configuration Update Failed',
          message:
            'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
          type: ToastType.ERROR,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
    onSettled: async (
      data,
      error,
      variables: CreatorsCampaignsWorkflowUpdateRequest,
    ) => {
      const invalidateQueriesPromises: Promise<void>[] = [
        queryClient.invalidateQueries({
          queryKey: [...queryKey, variables.campaignSlugName],
        }),
      ];
      if (variables.shouldSaveRevision) {
        invalidateQueriesPromises.push(
          queryClient.invalidateQueries({
            queryKey: [
              QueryKeyConstants.Campaigns,
              QueryKeyConstants.CreatorsCampaigns,
              QueryKeyConstants.CreatorsCampaignsInformation,
              variables.campaignSlugName,
            ],
          }),
        );
      }

      await Promise.all(invalidateQueriesPromises);
    },
  });
}

export function useCreatorsCampaignPreviewToken({
  campaignSlugName,
}: {
  campaignSlugName: string;
}) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CreatorsCampaigns,
      QueryKeyConstants.CreatorsCampaignWorkflow,
      QueryKeyConstants.CreatorsCampaignsPreviewToken,
      campaignSlugName,
    ],
    queryFn: async ({ signal }) => {
      const creatorCampaignPreviewTokenResponse =
        await getCreatorsCampaignsWorkflowPreviewToken(
          { campaignSlugName },
          { signal },
        );

      return creatorCampaignPreviewTokenResponse.token ?? null;
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
    enabled: isDefined(campaignSlugName),
    refetchInterval: 3_000_000, // referesh token every hour
    refetchIntervalInBackground: true,
  });
}

/** Use useCreatorsCampaignsMaxPageCreatorsQuery when fetching campaign creators to max page size to ensure we don't unnecessarily refetch */
export function useCreatorsCampaignsCreatorsListQuery<
  T = CreatorsCampaignsMembersListResponse,
>(
  {
    campaignSlugName,
    excludeExtraDetails = false,
    networks,
    pageNumber = DEFAULT_PAGE_NUMBER,
    pageSize,
    populateWithCreatorProfile,
    searchTerm,
    select,
  }: {
    campaignSlugName: string | undefined;
    excludeExtraDetails?: boolean | undefined;
    networks: SocialCreatorsPlatforms[];
    pageNumber?: number | undefined;
    pageSize?: number | undefined;
    populateWithCreatorProfile?: boolean | undefined;
    searchTerm?: string | undefined;
    select?: (data: CreatorsCampaignsMembersListResponse) => T;
  },
  enabled?: boolean,
) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CreatorsCampaigns,
      QueryKeyConstants.CreatorsCampaignsMembersList,
      {
        campaignSlugName,
        excludeExtraDetails,
        networks,
        pageNumber,
        pageSize,
        populateWithCreatorProfile,
        searchTerm,
      },
    ],
    enabled,
    queryFn: async ({ signal }) => {
      if (!isDefined(campaignSlugName)) {
        throw new Error('Undefined CampaignSlugName');
      }
      if (networks.length === 0) {
        throw new Error('At least one network is required');
      }
      const queryResults = await getCreatorsCampaignsMembersList(
        {
          campaignSlugName,
          excludeExtraDetails,
          networks,
          pageNumber,
          pageSize,
          populateWithCreatorProfile,
          searchTerm,
        },
        { signal },
      );
      return {
        ...queryResults,
        creators: queryResults.creators?.map((item) => ({
          ...item,
          creator: {
            ...item.creator,
            aggregateResponse: {
              currency: UNKNOWN_CURRENCY,
              ...item.creator?.aggregateResponse,
            },
          },
        })),
      };
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
    select,
  });
}

/** useCreatorsCampaignsCreatorsListQuery Wrapper for fetching creators to max page size   */
export const useCreatorsCampaignsMaxPageCreatorsQuery = <
  T = CreatorsCampaignsMembersListResponse,
>(
  {
    campaignSlugName,
    excludeExtraDetails,
    populateWithCreatorProfile,
    searchTerm,
    select,
  }: {
    campaignSlugName: string | undefined;
    excludeExtraDetails?: boolean | undefined;
    populateWithCreatorProfile?: boolean | undefined;
    searchTerm?: string | undefined;
    select?: (data: CreatorsCampaignsMembersListResponse) => T;
  },
  enabled?: boolean,
) => {
  return useCreatorsCampaignsCreatorsListQuery<T>(
    {
      campaignSlugName,
      excludeExtraDetails,
      networks: getNetworkListFromPlatformFilter(DEFAULT_ALL_SELECTION),
      pageNumber: DEFAULT_PAGE_NUMBER,
      pageSize: CAMPAIGN_CREATORS_LIST_PAGE_SIZE,
      populateWithCreatorProfile,
      searchTerm,
      select,
    },
    enabled,
  );
};

export function useCreatorsCampaignsProposalsListQuery<
  T = CreatorsCampaignsProposalsListResponse,
>(
  {
    campaignSlugName,
    pageNumber,
    pageSize,
    searchTerm,
  }: CreatorsCampaignsProposalsListsQueryRequest,
  select?: (data: CreatorsCampaignsProposalsListResponse) => T,
) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CreatorsCampaigns,
      QueryKeyConstants.CampaignsCreatorsProposalsList,
      { campaignSlugName, pageNumber, pageSize, searchTerm },
    ],
    queryFn: async ({ signal }) => {
      if (!isDefined(campaignSlugName)) {
        throw new Error(
          'Undefined CampaignSlugName. CampaignSlugName is required by useCreatorsCampaignsProposalsListQuery',
        );
      }
      return getCreatorsCampaignsProposalsList(
        { campaignSlugName, pageNumber, pageSize, searchTerm },
        { signal },
      );
    },
    enabled: isDefined(campaignSlugName),
    staleTime: FIVE_MINUTES_STALE_TIME,
    select,
  });
}

export function useInfluencersSearchQuery({
  network,
  searchTerm,
}: {
  searchTerm: string | undefined;
  network: SocialCreatorsPlatforms;
}) {
  return useQuery({
    enabled: !!searchTerm,
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CreatorsCampaignsInfluencersSearch,
      searchTerm,
      network,
    ],
    queryFn: async ({ signal }) => {
      const queryResults = await searchCreatorsCrossPlatform(
        {
          term: searchTerm,
          networks: [network],
          limit: SEARCH_INFLUENCERS_LIMIT,
          offset: 0,
        },
        { signal },
      );
      return queryResults;
    },
  });
}

export function useAddCreatorsToCampaignMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      campaignSlugName,
      creators,
    }: {
      campaignSlugName: string;
      creators: BasicCreatorsInfo[];
    }) => {
      return addCreatorsToCreatorsCampaigns({ campaignSlugName, creators });
    },
    onSettled: async (_, __, { campaignSlugName }) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [
            QueryKeyConstants.Campaigns,
            QueryKeyConstants.CreatorsCampaigns,
            QueryKeyConstants.CreatorsCampaignsMembersList,
            { campaignSlugName },
          ],
        }),
        queryClient.invalidateQueries({
          queryKey: [
            QueryKeyConstants.Campaigns,
            QueryKeyConstants.CreatorsCampaigns,
            QueryKeyConstants.CreatorsCampaignsInformation,
            campaignSlugName,
          ],
        }),
        queryClient.invalidateQueries({
          queryKey: [
            QueryKeyConstants.Campaigns,
            QueryKeyConstants.CreatorsCampaigns,
            QueryKeyConstants.CreatorsCampaignWorkflow,
            QueryKeyConstants.CreatorsCampaignsImpact,
          ],
        }),
      ]);
    },
  });
}

export function useUpdateCreatorMetadataMutation({
  campaignSlugName,
}: {
  campaignSlugName: string;
}) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      creator: CreatorsCampaignsUpdateCreatorMetadataRequest['creator'],
    ) => {
      return updateCreatorsCampaignsCreatorMetadata({
        campaignSlugName,
        creator,
      });
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [
          QueryKeyConstants.Campaigns,
          QueryKeyConstants.CreatorsCampaigns,
          QueryKeyConstants.CreatorsCampaignsMembersList,
          { campaignSlugName },
        ],
      });
    },
    onSuccess: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Creator Updated',
          message: "You've successfully updated this creator details.",
          type: ToastType.SUCCESS,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
    onError: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Creator Update Failed',
          message:
            'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
          type: ToastType.ERROR,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
  });
}

export function useDeletePendingCreatorsFromCampaignMutation() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: ({
      campaignSlugName,
      usernames,
    }: {
      campaignSlugName: string;
      usernames: string[];
    }) => {
      return deletePendingCreators({ campaignSlugName, usernames });
    },
    onSettled: async (_, __, { campaignSlugName }) => {
      await queryClient.invalidateQueries({
        queryKey: [
          QueryKeyConstants.Campaigns,
          QueryKeyConstants.CreatorsCampaigns,
          QueryKeyConstants.CreatorsCampaignsInformation,
          campaignSlugName,
        ],
      });
    },
    onSuccess: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Pending Creators Removed',
          message: "You've successfully removed pending creators.",
          type: ToastType.SUCCESS,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
    onError: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Pending Creators Removal Failed',
          message:
            'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
          type: ToastType.ERROR,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
  });
}

export function useDeleteCreatorsFromCampaignMutation() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: ({
      campaignSlugName,
      creators,
    }: {
      campaignSlugName: string;
      creators: BasicCreatorsInfo[];
    }) => {
      return deleteCreators({ campaignSlugName, creators });
    },
    onSettled: async (_, __, { campaignSlugName }) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [
            QueryKeyConstants.Campaigns,
            QueryKeyConstants.CreatorsCampaigns,
            QueryKeyConstants.CreatorsCampaignsMembersList,
            { campaignSlugName },
          ],
        }),
        queryClient.invalidateQueries({
          queryKey: [
            QueryKeyConstants.CreatorCommunity,
            QueryKeyConstants.CreatorCommunityApplications,
            { status: 'APPROVED' },
          ],
        }),
        queryClient.invalidateQueries({
          queryKey: [
            QueryKeyConstants.Campaigns,
            QueryKeyConstants.CreatorsCampaigns,
            QueryKeyConstants.CreatorsCampaignWorkflow,
            QueryKeyConstants.CreatorsCampaignsImpact,
          ],
        }),
      ]);
    },
    onSuccess: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Creator Removed',
          message: "You've successfully removed the creator.",
          type: ToastType.SUCCESS,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
    onError: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Creator Removal Failed',
          message:
            'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
          type: ToastType.ERROR,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
  });
}

export function useDeleteCreatorsCampaignContentMutation() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: ({
      campaignSlugName,
      contentIds,
    }: {
      campaignSlugName: string;
      contentIds: PartialCuid[];
    }) => {
      return deleteCreatorsCampaignContent({ campaignSlugName, contentIds });
    },
    onSettled: async (_, __, { campaignSlugName }) => {
      await queryClient.invalidateQueries({
        queryKey: [
          QueryKeyConstants.Campaigns,
          QueryKeyConstants.CreatorsCampaigns,
          QueryKeyConstants.CreatorsCampaignsInformation,
          campaignSlugName,
        ],
      });
    },
    onSuccess: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Content Deleted',
          message: "You've successfully deleted the content from the campaign.",
          type: ToastType.SUCCESS,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
    onError: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Content Deletion Failed',
          message:
            'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
          type: ToastType.ERROR,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
  });
}

export function useCreatorNotesQuery({
  network,
  username,
}: {
  username: string | undefined;
  network: SocialCreatorsPlatforms | undefined;
}) {
  if (!isDefined(username)) {
    throw new Error('Undefined Username');
  }

  if (!isDefined(network)) {
    throw new Error('Undefined Network');
  }

  return useQuery({
    queryKey: [QueryKeyConstants.CreatorNotes, username, network],
    queryFn: async ({ signal }) => {
      const queryResults = await getCreatorNotes(
        { username, network },
        { signal },
      );
      return queryResults;
    },
    enabled: !!(network && username),
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
}

export function useAddCreatorNotesMutation({
  network,
  username,
}: {
  username: string | undefined;
  network: SocialCreatorsPlatforms | undefined;
}) {
  if (!isDefined(username)) {
    throw new Error('Undefined Username');
  }

  if (!isDefined(network)) {
    throw new Error('Undefined Network');
  }

  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: (note: string) => {
      return addCreatorNotes({ username, network, note });
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [QueryKeyConstants.CreatorNotes, username, network],
      });
    },
    onSuccess: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'New Note Added',
          message: "You've successfully added a new note.",
          type: ToastType.SUCCESS,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
    onError: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'New Note Creation Failed',
          message:
            'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
          type: ToastType.ERROR,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
  });
}

export function useDeleteCreatorNoteMutation() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: ({
      noteId,
    }: {
      noteId: string;
      username: string;
      network: SocialCreatorsPlatforms;
    }) => {
      return deleteCreatorNote({ noteId });
    },
    onSettled: async (_, __, { network, username }) => {
      await queryClient.invalidateQueries({
        queryKey: [QueryKeyConstants.CreatorNotes, username, network],
      });
    },
    onSuccess: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Note Deleted',
          message: "You've successfully deleted the note.",
          type: ToastType.SUCCESS,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
    onError: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Note Deletion Failed',
          message:
            'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
          type: ToastType.ERROR,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
  });
}

export function useEditCreatorNoteMutation() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: ({
      note,
      noteId,
    }: {
      note: string;
      noteId: string;
      username: string;
      network: SocialCreatorsPlatforms;
    }) => {
      return editCreatorNote({ note, noteId });
    },
    onMutate: async ({ network, note, noteId, username }) => {
      const previousNotes: CreatorNotesResponse[] | undefined =
        queryClient.getQueryData([
          QueryKeyConstants.CreatorNotes,
          username,
          network,
        ]);

      if (previousNotes) {
        const newNotes = previousNotes.map(
          (previousNote: CreatorNotesResponse) => {
            if (previousNote.id === noteId) {
              return { ...previousNote, note };
            }
            return previousNote;
          },
        );

        queryClient.setQueryData(
          [QueryKeyConstants.CreatorNotes, username, network],
          newNotes,
        );
        return { previousNotes };
      }
    },
    onSettled: async (_, __, { network, username }) => {
      await queryClient.invalidateQueries({
        queryKey: [QueryKeyConstants.CreatorNotes, username, network],
      });
    },
    onSuccess: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Note Edited',
          message: "You've successfully edited the note.",
          type: ToastType.SUCCESS,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
    onError: (err, { network, username }, context) => {
      if (isDefined(context?.previousNotes)) {
        queryClient.setQueryData(
          [QueryKeyConstants.CreatorNotes, username, network],
          context.previousNotes,
        );
      }
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Note Editing Failed',
          message:
            'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
          type: ToastType.ERROR,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
  });
}

export function useCreateCreatorsClientCampaignMutation() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: async (creatorsCampaignData: UpsertCreatorsCampaignRequest) => {
      return createCreatorsClientCampaign(creatorsCampaignData);
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [
            QueryKeyConstants.Campaigns,
            QueryKeyConstants.CreatorsCampaigns,
            QueryKeyConstants.CreatorsCampaignsInformation,
          ],
        }),
        queryClient.invalidateQueries({
          queryKey: [
            QueryKeyConstants.Campaigns,
            QueryKeyConstants.CreatorsCampaigns,
            QueryKeyConstants.CreatorsCampaignsList,
          ],
        }),
      ]);
    },
    onSuccess: ({ campaignSlugName }) => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Created New Campaign',
          message: "You've successfully created a new campaign.",
          type: ToastType.SUCCESS,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
      void navigateToState('campaigns.creators-configuration-manage', {
        campaignSlugName,
      });
    },
    onError: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'New Campaign Creation Failed',
          message: 'Something went wrong, while creating a new campaign.',
          type: ToastType.ERROR,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
  });
}

export function useDuplicateCreatorsClientCampaignMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      creatorsCampaignData: DuplicateCreatorsCampaignRequest,
    ) => {
      return duplicateCreatorsCampaign(creatorsCampaignData);
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [
            QueryKeyConstants.Campaigns,
            QueryKeyConstants.CreatorsCampaigns,
            QueryKeyConstants.CreatorsCampaignsInformation,
          ],
        }),
        queryClient.invalidateQueries({
          queryKey: [
            QueryKeyConstants.Campaigns,
            QueryKeyConstants.CreatorsCampaigns,
            QueryKeyConstants.CreatorsCampaignsList,
          ],
        }),
      ]);
    },
  });
}

export function useCreatorsCampaignsListQuery(
  req: ActiveCreatorsCampaignsListRequest,
) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CreatorsCampaigns,
      QueryKeyConstants.CreatorsCampaignsList,
      req,
    ],
    queryFn: async ({ signal }) => {
      const queryResults = await getCreatorsCampaignsList(req, { signal });
      return queryResults;
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
    placeholderData: keepPreviousData,
  });
}

export function useCreatorsCampaignsMetricsQuery(enabled?: boolean) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CreatorsCampaigns,
      QueryKeyConstants.CreatorsCampaignsList,
      QueryKeyConstants.CreatorsCampaignsMetrics,
    ],
    queryFn: async () => getCreatorsCampaignsMetrics(),
    staleTime: FIVE_MINUTES_STALE_TIME,
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useUpdateCreatorsClientCampaignMutation() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: async (creatorsCampaignData: UpsertCreatorsCampaignRequest) => {
      return updateCreatorsClientCampaign(creatorsCampaignData);
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [
            QueryKeyConstants.Campaigns,
            QueryKeyConstants.CreatorsCampaigns,
            QueryKeyConstants.CreatorsCampaignsInformation,
          ],
        }),
        queryClient.invalidateQueries({
          queryKey: [
            QueryKeyConstants.Campaigns,
            QueryKeyConstants.CreatorsCampaigns,
            QueryKeyConstants.CreatorsCampaignsList,
          ],
        }),
      ]);
    },
    onSuccess: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Campaign details Updated',
          message: "You've successfully updated the campaign details.",
          type: ToastType.SUCCESS,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
  });
}

export function useDeleteCreatorsClientCampaignMutation() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: async (campaignSlugName: string) => {
      return deleteCreatorsCampaign({ campaignSlugName });
    },
    onSettled: async () => {
      queryClient.removeQueries({
        queryKey: [
          QueryKeyConstants.Campaigns,
          QueryKeyConstants.CreatorsCampaigns,
          QueryKeyConstants.CreatorsCampaignsInformation,
        ],
      });
      await queryClient.invalidateQueries({
        queryKey: [
          QueryKeyConstants.Campaigns,
          QueryKeyConstants.CreatorsCampaigns,
          QueryKeyConstants.CreatorsCampaignsList,
        ],
      });
    },
    onSuccess: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Creator Campaign Deleted',
          message: "You've successfully deleted the campaign.",
          type: ToastType.SUCCESS,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
      void navigateToState('campaigns.creators');
    },
    onError: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Creator Campaign Delete Failed',
          message:
            'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
          type: ToastType.ERROR,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
  });
}

export function useCreatorsCampaignsProposalDetailsQuery({
  campaignSlugName,
  creatorId,
}: Partial<CreatorsCampaignsProposalDetailsRequest>) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CreatorsCampaigns,
      QueryKeyConstants.CampaignsCreatorsProposalsList,
      QueryKeyConstants.CampaignsCreatorsProposalDetails,
      { campaignSlugName, creatorId },
    ],
    queryFn: async ({ signal }) => {
      if (!isDefined(campaignSlugName)) {
        throw new Error(
          'Undefined CampaignSlugName. CampaignSlugName is required by useCreatorsCampaignsProposalDetailsQuery',
        );
      }

      if (!isDefined(creatorId)) {
        throw new Error(
          'Undefined CreatorId. CreatorId is required by useCreatorsCampaignsProposalDetailsQuery',
        );
      }

      const queryResults = await getCreatorsCampaignsProposalDetails(
        { campaignSlugName, creatorId },
        { signal },
      );

      return queryResults;
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
    enabled: isDefined(campaignSlugName) && isDefined(creatorId),
  });
}

export function useCreatorsCampaignsOrdersListQuery<
  T = Partial<CreatorsCampaignsOrdersListResponse>,
>(
  {
    campaignSlugName,
    pageNumber,
    pageSize,
    searchTerm,
    sortCriteria,
    sortDirection,
  }: Partial<CreatorsCampaignsOrdersListQueryRequest>,
  select?: (data: CreatorsCampaignsOrdersListResponse) => T,
) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CreatorsCampaigns,
      QueryKeyConstants.CampaignsCreatorsOrdersList,
      {
        campaignSlugName,
        pageNumber,
        pageSize,
        searchTerm,
        sortCriteria,
        sortDirection,
      },
    ],
    queryFn: async ({ signal }) => {
      if (!isDefined(campaignSlugName)) {
        throw new Error(
          'Undefined CampaignSlugName. CampaignSlugName is required by useCreatorsCampaignsOrdersListQuery',
        );
      }
      return getCreatorsCampaignsOrdersList(
        {
          campaignSlugName,
          pageNumber,
          pageSize,
          searchTerm,
          sortCriteria,
          sortDirection,
        },
        { signal },
      );
    },
    enabled: isDefined(campaignSlugName),
    staleTime: FIVE_MINUTES_STALE_TIME,
    select,
  });
}

export function useCreateCreatorsCampaignsOrderMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreatorsCampaignsCreateOrderRequest) => {
      return createCreatorsCampaignsOrder(payload);
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [
            QueryKeyConstants.Campaigns,
            QueryKeyConstants.CreatorsCampaigns,
            QueryKeyConstants.CampaignsCreatorsProposalsList,
          ],
        }),
        queryClient.invalidateQueries({
          queryKey: [
            QueryKeyConstants.Campaigns,
            QueryKeyConstants.CreatorsCampaigns,
            QueryKeyConstants.CampaignsCreatorsOrdersList,
          ],
        }),
      ]);
    },
  });
}

export function useCreatorsCampaignsOrderDetailsQuery({
  campaignSlugName,
  orderId,
}: Partial<CreatorsCampaignsOrderDetailsRequest>) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CreatorsCampaigns,
      QueryKeyConstants.CampaignsCreatorsOrdersList,
      QueryKeyConstants.CampaignsCreatorsOrderDetails,
      { campaignSlugName, orderId },
    ],
    queryFn: async ({ signal }) => {
      if (!isDefined(campaignSlugName) || !isDefined(orderId)) {
        throw new Error(
          'Undefined CampaignSlugName or OrderId. CampaignSlugName and OrderId is required by useCreatorsCampaignsOrderDetailsQuery',
        );
      }

      const queryResults = await getCreatorsCampaignsOrderDetails(
        { campaignSlugName, orderId },
        { signal },
      );

      return queryResults;
    },
    enabled: isDefined(campaignSlugName) && isDefined(orderId),
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
}

export function useUpdateCampaignProposalWithAffiliatesMutation({
  campaignSlugName,
  campaignWorkflow,
  creatorId,
  creatorInfo,
  proposalData,
}: CreatorsCampaignsProposalDetailsRequest & {
  proposalData?: CreatorsCampaignsProposalDetailsResponse;
  creatorInfo?: CreatorsCampaignsMemberInfo;
  campaignWorkflow?: CreatorsCampaignsWorkflowResponse;
}) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      isFromIntegratedEmail,
      proposalRemarkByBrand,
      proposalStatus,
      senderEmail,
    }: CreatorsCampaignsProposalStatusRequestBase) => {
      const baseRequestBody: CreatorsCampaignsProposalStatusRequestBase = {
        isFromIntegratedEmail,
        senderEmail,
        proposalStatus,
      };

      const requestBodyWithRemark =
        proposalStatus === 'rejected'
          ? {
              ...baseRequestBody,
              proposalRemarkByBrand:
                proposalRemarkByBrand === ''
                  ? 'Not Specified'
                  : proposalRemarkByBrand,
            }
          : baseRequestBody;

      const getAffiliateType = (): AffiliateType | undefined => {
        if (proposalData?.googleAnalyticsConfiguration?.enabled) {
          return AffiliateType.GOOGLE_ANALYTICS;
        }
        if (campaignWorkflow?.storeConfigurations?.pricingRule?.pricingRuleId) {
          return AffiliateType.SHOPIFY;
        }
        if (campaignWorkflow?.impact?.enabled) {
          return AffiliateType.IMPACT;
        }
      };

      const requestBodyWithAffiliates: CreatorsCampaignsProposalStatusRequestBase =
        proposalStatus === 'approved'
          ? (() => {
              const fullName = isDefined(creatorInfo?.name)
                ? parseFullName(creatorInfo.name, creatorInfo.username)
                : undefined;

              const destinationUrl =
                proposalData?.googleAnalyticsConfiguration?.destinationUrl ??
                '';
              const affiliateType = getAffiliateType();

              return {
                ...requestBodyWithRemark,
                ...(affiliateType
                  ? {
                      affiliateDetails: {
                        affiliateType,
                        ...(!isEmptyValue(destinationUrl) && {
                          url: destinationUrl,
                        }),
                      },
                      creator: {
                        username: creatorInfo?.username,
                        firstName: fullName?.firstName,
                        lastName: fullName?.lastName,
                        network: creatorInfo?.network,
                      },
                    }
                  : {}),
              };
            })()
          : requestBodyWithRemark;

      return updateCreatorsCampaignsProposalStatusWithAffiliates({
        campaignSlugName,
        creatorId,
        requestBody: requestBodyWithAffiliates,
      });
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [
          QueryKeyConstants.Campaigns,
          QueryKeyConstants.CreatorsCampaigns,
        ],
      });
    },
    onSuccess: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Proposal Status Updated',
          message: "You've successfully updated the proposal status.",
          type: ToastType.SUCCESS,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
    onError: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Proposal Status Update Failed',
          message:
            'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
          type: ToastType.ERROR,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
  });
}

export function useCreatorsCampaignsProposalOrderEligibleProductsQuery({
  campaignSlugName,
  creatorId,
}: Partial<CreatorsCampaignsProposalDetailsRequest>) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CreatorsCampaigns,
      QueryKeyConstants.CampaignsCreatorsProposalsList,
      QueryKeyConstants.CampaignsCreatorsProposalDetails,
      QueryKeyConstants.CampaignsCreatorsOrderEligibleProductsList,
      { campaignSlugName, creatorId },
    ],
    queryFn: async ({ signal }) => {
      if (!isDefined(campaignSlugName)) {
        throw new Error(
          'CampaignSlugName is not defined. CampaignSlugName is required by useCreatorsCampaignsProposalOrderEligibleProductsQuery',
        );
      }

      if (!isDefined(creatorId)) {
        throw new Error(
          'Undefined CreatorId. CreatorId is required by useCreatorsCampaignsProposalOrderEligibleProductsQuery',
        );
      }

      const queryResults =
        await getCreatorsCampaignsProposalOrderEligibleProducts(
          { campaignSlugName, creatorId },
          { signal },
        );

      return queryResults;
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
    enabled: isDefined(campaignSlugName) && isDefined(creatorId),
  });
}

/**
 * Infinite query to fetch creators campaign content
 * @param query.offset - optional offset for pagination. If offset is specified, we will not do infinite query
 */
export const useCreatorsCampaignContentInfiniteQuery = <
  T = IContentLibraryItemsResponse,
>(
  query: IContentLibraryItemParams,
  select?: (
    data: InfiniteData<IContentLibraryItemsResponse>,
  ) => InfiniteData<T>,
  enabled: boolean = true,
) => {
  const limit = query.limit ?? DEFAULT_GALLERY_PAGE_SIZE;
  const fetchContentItems = async (page = 0, signal?: AbortSignal) => {
    const data = await getContentLibraryItems(
      {
        ...query,
        limit,
        offset: query.offset ?? (page > 0 ? (page - 1) * limit : 0),
        shouldHydrateAdDetails: true,
        hydrateWithDeetsItems: true,
      },
      { signal },
    );

    return {
      ...data,
      // TODO: PD-283868: replace default currency on frontend with currency sourced from services on gateway
      contentStats: { currency: UNKNOWN_CURRENCY, ...data.contentStats },
    };
  };

  const queryKey = [
    QueryKeyConstants.Campaigns,
    QueryKeyConstants.CreatorsCampaigns,
    QueryKeyConstants.CreatorsCampaignsContents,
    query,
  ] as const;

  return useInfiniteQuery<
    IContentLibraryItemsResponse,
    Error,
    InfiniteData<T>,
    typeof queryKey,
    number
  >({
    queryKey,
    queryFn: ({ pageParam, signal }) => fetchContentItems(pageParam, signal),
    getNextPageParam: (last, pages) =>
      last.contentItems.length < limit ? undefined : pages.length + 1,
    initialPageParam: 0,
    staleTime: FIVE_MINUTES_STALE_TIME,
    select,
    enabled,
  });
};

export function useCreatorsCampaignSentimentsReportAggregateQuery<
  T = CreatorsCampaignsReportQueryResponse,
>(
  query: IContentLibraryItemParams,
  campaignSlugName: string,
  select?: (data: CreatorsCampaignsReportQueryResponse | null) => T,
) {
  const getAggregatedReport = async (signal?: AbortSignal) => {
    const LIMIT = 200;
    const MAX_ITERATIONS = 20;
    const fetchContentItems = async (
      allContentItems: IContentLibraryItem[],
      offset: number,
      i: number,
      signal?: AbortSignal,
    ): Promise<IContentLibraryItem[]> => {
      const data = await getContentLibraryItems(
        {
          ...query,
          limit: LIMIT,
          offset,
        },
        { signal },
      );

      const newContentItems = allContentItems.concat(data.contentItems);

      if (data.contentItems.length < LIMIT || i > MAX_ITERATIONS) {
        return newContentItems;
      } else {
        return fetchContentItems(
          newContentItems,
          offset + LIMIT,
          i + 1,
          signal,
        );
      }
    };

    const allContentItems = await fetchContentItems([], 0, 0, signal);
    const supportedCampaignNetworks = CAMPAIGN_REPORT_NETWORKS.map((n) =>
      n.toLowerCase(),
    );
    const filteredContentItem = allContentItems.filter((contentItem) => {
      const isNetworkSupported = supportedCampaignNetworks.includes(
        contentItem.socialDetails?.socialNetwork?.toLowerCase() || '',
      );

      return (
        isNetworkSupported &&
        contentItem.socialDetails?.isInstagramStory !== true
      );
    });
    const mediaIds = filteredContentItem.map((item) => item.id);

    if (!mediaIds.length) {
      return;
    }

    const [aggregatedReport, report] = await Promise.all(
      [true, false].map((isAggregation) =>
        getCreatorsCampaignsReport(
          {
            campaignSlugName,
            mediaIds,
            isAggregation,
            platform: 'CROSS',
          },
          { signal },
        ),
      ),
    );

    if (aggregatedReport) {
      aggregatedReport.commentAnalysis = mediaIds.map((mediaId) => {
        const analysis = report?.commentAnalysis?.find(
          (media) => media.id === mediaId,
        );
        return analysis ?? {};
      });
    }

    return {
      ...aggregatedReport,
      contentItems: filteredContentItem,
    };
  };

  return useQuery({
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CreatorsCampaigns,
      QueryKeyConstants.CreatorsCampaignsReportAggregation,
      query,
    ],
    select,
    queryFn: async ({ signal }) => {
      const report = await getAggregatedReport(signal);
      return report ?? null;
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
}

export function useCreatorPortalLinkQuery({
  campaignSlugName,
  creatorId,
  enabled,
}: {
  campaignSlugName: string | undefined;
  creatorId: string | undefined;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CreatorsCampaigns,
      QueryKeyConstants.CreatorsCampaignsInformation,
      QueryKeyConstants.CreatorsCampaignsCreatorPortalLink,
      campaignSlugName,
      creatorId,
    ],
    queryFn: async () => {
      if (!isDefined(creatorId)) {
        throw new Error(
          'Creator Id is not defined in useCreatorPortalLinkQuery',
        );
      }
      if (!isDefined(campaignSlugName)) {
        throw new Error(
          'CampaignSlugName is not defined in useCreatorPortalLinkQuery',
        );
      }

      return getCreatorPortalLink({ campaignSlugName, creatorId });
    },
    enabled: enabled && isDefined(creatorId) && isDefined(campaignSlugName),
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
}

export function useExportCreatorPortalLinkQuery({
  campaignSlugName,
  creatorsIds,
}: {
  campaignSlugName: string | undefined;
  creatorsIds?: string[];
}) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CreatorsCampaigns,
      QueryKeyConstants.CreatorsCampaignsInformation,
      QueryKeyConstants.CreatorsCampaignsCreatorPortalLink,
      { campaignSlugName, creatorsIds },
    ],
    queryFn: async () => {
      if (!isDefined(campaignSlugName)) {
        throw new Error(
          'CampaignSlugName is not defined in useExportCreatorPortalLinkQuery',
        );
      }

      const blob = await exportCreatorPortalLinkCSV({
        campaignSlugName,
        creatorsIds,
      });

      return blob ?? null;
    },
    enabled: false,
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
}

export function useGetContentApprovalLinkQuery({
  campaignSlugName,
  creatorId,
}: {
  campaignSlugName: string | undefined;
  creatorId: string | undefined;
}) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CreatorsCampaigns,
      QueryKeyConstants.CreatorsCampaignsInformation,
      QueryKeyConstants.CreatorsCampaignsContentApprovalLinkGet,
      campaignSlugName,
      creatorId,
    ],
    queryFn: async ({ signal }) => {
      if (!isDefined(campaignSlugName)) {
        throw new Error(
          'CampaignSlugName is not defined in useGetContentApprovalLinkQuery',
        );
      }
      if (!isDefined(creatorId)) {
        throw new Error(
          'creatorId is not defined in useGetContentApprovalLinkQuery',
        );
      }

      return getCreatorContentApprovalLink(
        { campaignSlugName, creatorId },
        {
          signal,
        },
      );
    },
    enabled: false,
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
}

export function useExportContentApprovalLinkQuery({
  campaignSlugName,
  creatorIds,
}: {
  campaignSlugName: string | undefined;
  creatorIds?: string[];
}) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CreatorsCampaigns,
      QueryKeyConstants.CreatorsCampaignsInformation,
      QueryKeyConstants.CreatorsCampaignsContentApprovalLinkExport,
      { campaignSlugName, creatorIds },
    ],
    queryFn: async ({ signal }) => {
      if (!isDefined(campaignSlugName)) {
        throw new Error(
          'CampaignSlugName is not defined in useExportContentApprovalLinkQuery',
        );
      }

      const blob = await exportCreatorContentApprovalLinksCSV(
        {
          campaignSlugName,
          creatorIds,
        },
        { signal },
      );

      return blob ?? null;
    },
    enabled: false,
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
}

export function useGetCreatorPortalLinksQuery({
  campaignSlugName,
  creatorsIds,
}: {
  campaignSlugName: string | undefined;
  creatorsIds?: string[];
}) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CreatorsCampaigns,
      QueryKeyConstants.CreatorsCampaignsInformation,
      QueryKeyConstants.CreatorsCampaignsCreatorPortalLink,
      { campaignSlugName, creatorsIds },
    ],
    queryFn: async () => {
      if (!isDefined(campaignSlugName)) {
        throw new Error(
          'CampaignSlugName is not defined in useExportCreatorPortalLinkQuery',
        );
      }

      return getCreatorPortalLinks({ campaignSlugName, creatorsIds });
    },
    enabled: false,
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
}

export function useCreatorsCampaignsProposalDetailsCSVQuery({
  campaignSlugName,
}: {
  campaignSlugName: string | undefined;
}) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CreatorsCampaigns,
      QueryKeyConstants.CreatorsCampaignsInformation,
      QueryKeyConstants.CreatorsCampaignsProposalsDetailsCSV,
      campaignSlugName,
    ],
    queryFn: async () => {
      if (!isDefined(campaignSlugName)) {
        throw new Error(
          'CampaignSlugName is not defined in useCreatorsCampaignsProposalDetailsCSVQuery',
        );
      }
      // Will be triggered on demand with refetch
      const blob = await getCreatorsCampaignsProposalsDetailsCSV({
        campaignSlugName,
      });

      return blob ?? null;
    },
    enabled: false,
  });
}

export function useGetCampaignSparkCodeQuery(
  req: GetCampaignSparkCodeRequest,
  enabled: boolean,
) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CreatorsCampaigns,
      QueryKeyConstants.CreatorsCampaignsSparkCodeData,
      req,
    ],
    queryFn: async () => {
      if (!isDefined(req.campaignSlugName) || !isDefined(req.videoId)) {
        throw new Error(
          `A value in the request to useGetCampaignSparkCode is not defined (${JSON.stringify(req)})`,
        );
      }

      return getCampaignSparkCode(req);
    },
    enabled,
    staleTime: FIVE_MINUTES_STALE_TIME,
    retry: false,
  });
}

export function useTTOVideoLinkRequestMutation(
  req: LinkTiktokVideoToTTOCampaignRequest,
) {
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: async () => {
      if (isUndefined(req.campaignSlugName)) {
        throw new Error('Undefined campaignSlugName');
      }

      if (isUndefined(req.campaignId)) {
        throw new Error('Undefined TTO campaignId');
      }

      if (isUndefined(req.videoId)) {
        throw new Error('Undefined videoId');
      }

      return linkTiktokVideoToTTOCampaign(req);
    },
    onSuccess: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'TTO Video Link Request',
          message:
            'TTO Video Link Request sent successfully. The creator will be notified of the request in the TikTok app.',
          type: ToastType.SUCCESS,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
    onError: (error) => {
      const errorMessage = getRequestErrorMessage(error);

      const clientErrorMessage = (() => {
        if (errorMessage === 'Link already requested in the past 24 hours') {
          return 'Link already requested in the past 24 hours.';
        } else if (
          errorMessage ===
          'Invalid param: Link request status is Reject. No duplicated request allowed.'
        ) {
          return 'Link request status is rejected. No duplicated request allowed.';
        } else if (
          errorMessage ===
          'Invalid param: video is unable to link. Reason: The video has been linked to a campaign.'
        ) {
          return 'Video is already linked to a campaign.';
        } else {
          return 'Video Link Request failed. Please try again or contact Bazaarvoice Support.';
        }
      })();

      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'TTO Video Link Request Failed',
          message: clientErrorMessage,
          type: ToastType.ERROR,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
  });
}

export function useRequestSparkCodeMutation(
  campaignSlugName: string | undefined,
  itemId: ItemId | undefined,
) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (authorizationDays: number) => {
      if (!isDefined(campaignSlugName) || !isDefined(itemId)) {
        throw new Error(
          'campaignSlugName and/or itemId is undefined in useRequestSparkCodeMutation',
        );
      }
      return postRequestSparkCodeAuthorization({
        campaignSlugName,
        itemId,
        authorizationDays,
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: [
          QueryKeyConstants.Campaigns,
          QueryKeyConstants.CreatorsCampaigns,
          QueryKeyConstants.CreatorsCampaignsSparkCodeData,
          campaignSlugName,
          itemId,
        ],
      });
    },
    onSuccess: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Spark Ads Request',
          message:
            'Request sent successfully. Once the creator approves the Spark Ads request, you can find the Spark Ads Code by clicking (...) on the respective content card.',
          type: ToastType.SUCCESS,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
      if (isDefined(campaignSlugName)) {
        void navigateToState(
          'campaigns.creators-contents-social-contents-media',
          {
            campaignSlugName,
          },
        );
      }
    },
    onError: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Spark Ads Request Failed',
          message:
            'An error occurred while sending the request. Please try again.',
          type: ToastType.ERROR,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
  });
}

export function useGetTiktokAdAccountsQuery() {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CreatorsCampaigns,
      QueryKeyConstants.CreatorsCampaignsSparkCodeData,
    ],
    queryFn: async ({ signal }) => {
      return getTiktokAdAccounts({ signal });
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
}

export function useApplySparkCodeMutation(
  advertiserIds: string[],
  campaignSlugName: string | undefined,
  sparkCode: string | undefined,
) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => {
      if (!isDefined(campaignSlugName) || !isDefined(sparkCode)) {
        throw new Error(
          'campaignSlugName and/or sparkCode were undefined in useApplySparkCodeMutation',
        );
      }
      return applySparkCode({
        sparkCode,
        advertiserIds,
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: [
          QueryKeyConstants.Campaigns,
          QueryKeyConstants.CreatorsCampaigns,
          QueryKeyConstants.CreatorsCampaignsSparkCodeData,
        ],
      });
    },
    onSuccess: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Apply Spark Ads',
          message:
            'Please visit TikTok Ad Manager to configure and run Spark Ads for this content.',
          type: ToastType.SUCCESS,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
      if (campaignSlugName) {
        void navigateToState(
          'campaigns.creators-contents-social-contents-media',
          {
            campaignSlugName,
          },
        );
      }
    },
    onError: (error: RequestError) => {
      const errorMessage = getRequestErrorMessage(error);
      const clientErrorMessage = (() => {
        if (errorMessage === 'Invalid authorization code.') {
          return 'Spark code invalid. Please reach out to the creator to get updated code.';
        } else if (
          errorMessage ===
          'The authorization code has expired. Please use a new authorization code.'
        ) {
          return 'Spark code expired. Please generate a new Spark code.';
        } else {
          return 'An error occurred while sending the request. Please try again.';
        }
      })();
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Apply Spark Ads',
          message: clientErrorMessage,
          type: ToastType.ERROR,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
  });
}

export function useShopifyPricingRulesQuery(
  req?: ShopifyPricingRuleRequest,
  enabled?: boolean,
) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CreatorsCampaigns,
      QueryKeyConstants.Shopify,
      QueryKeyConstants.ShopifyPricingRules,
      req,
    ],
    enabled,
    queryFn: async ({ signal }) => {
      return getShopifyPricingRules(req, { signal });
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
}

export function useCreatorsCampaignsAffiliateOrdersDetailsQuery(
  req: CreatorsCampaignsAffiliateOrdersRequest,
) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CreatorsCampaigns,
      QueryKeyConstants.CreatorsCampaignsAffiliateOrders,
      req,
    ],
    queryFn: async () => {
      return getCreatorsCampaignsAffiliateOrdersDetails(req);
    },
    enabled: isDefined(req.campaignSlugName) && isDefined(req.affiliateCode),
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
}

export function useCreatorsCampaignsAffiliatesListQuery(
  req: CreatorsCampaignsAffiliatesListRequest,
  enabled?: boolean,
) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CreatorsCampaigns,
      QueryKeyConstants.CreatorsCampaignsAffiliatesList,
      req,
    ],
    queryFn: async () => {
      if (!isDefined(req.campaignSlugName)) {
        throw new Error(
          'CampaignSlugName is required in useCreatorsCampaignsAffiliatesListQuery',
        );
      }

      return getCreatorsCampaignsAffiliatesList(req);
    },
    enabled: enabled && isDefined(req.campaignSlugName),
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
}

export function useCreatorsCampaignsAffiliatesGoogleAnalyticsConfigurationQuery<
  T = CreatorsCampaignsAffiliatesGoogleAnalyticsConfigurationResponse,
>(
  req: CreatorsCampaignsAffiliatesGAConfigurationRequest,
  enabled: boolean = true,
  select?: (
    data: CreatorsCampaignsAffiliatesGoogleAnalyticsConfigurationResponse,
  ) => T,
) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CreatorsCampaigns,
      QueryKeyConstants.CreatorsCampaignsAffiliatesGoogleAnalyticsConfiguration,
      req,
    ],
    select,
    queryFn: async () => {
      return getCreatorsCampaignsAffiliatesGAConfigurationList(req);
    },
    enabled: enabled && isDefined(req.campaignSlugName),
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
}

export function useCreatorsCampaignsAffiliatesAggregateStatsQuery<
  T = CreatorsCampaignsAffiliatesAggregateStatsResponse,
>(
  req: Omit<GetCreatorsCampaignAffiliatesStatsRequest, 'campaignSlugName'> & {
    campaignSlugName: string | undefined;
  },
  enabled = true,
  select?: (data: CreatorsCampaignsAffiliatesAggregateStatsResponse) => T,
) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CreatorsCampaigns,
      QueryKeyConstants.CreatorsCampaignsAffiliatesAggregateStats,
      req,
    ],
    queryFn: async () => {
      const { campaignSlugName, ...rest } = req;
      if (!isDefined(campaignSlugName)) {
        throw new Error(
          'CampaignSlugName is required in useCreatorsCampaignsAffiliatesAggregateStatsQuery',
        );
      }
      return getCreatorsCampaignsAffiliatesAggregateStats({
        campaignSlugName,
        ...rest,
      });
    },
    enabled: enabled && isDefined(req.campaignSlugName),
    select,
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
}

export function useCampaignAnalyticsStats<T = CampaignAnalyticsStatsResponse[]>(
  req: CampaignAnalyticsStatsRequest,
  enabled?: boolean,
  select?: (data: CampaignAnalyticsStatsResponse[]) => T,
) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CreatorsCampaigns,
      QueryKeyConstants.CampaignAnalyticsStats,
      req,
    ],
    queryFn: async ({ signal }) => {
      const { endDate, startDate, ...rest } = req;

      const data = await getCampaignAnalyticsStats(
        {
          ...rest,
          ecommerceSessionType: req.ecommerceSessionType,
          interval: Interval.fromDateRange({
            alias: 'custom',
            startDate: isDefined(startDate)
              ? startDate
              : CAMPAIGNS_UNIFIED_METRICS_STATS_EARLIEST_DATE,
            endDate: getValidCampaignEndDate(startDate, endDate),
          }),
        },
        { signal },
      );
      return data;
    },
    enabled,
    select,
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
}

// serialization select function for IContentLibraryItemsResponse
export function getDeserializedItemsResponse(
  data: InfiniteData<IContentLibraryItemsResponse>,
): InfiniteData<IDeserializedContentLibraryItemsResponse> {
  const transformedPages = data.pages.map((page) => {
    const { contentItems, ...rest } = page;

    return {
      ...rest,
      contentItems: deserializeContentItems(contentItems),
    };
  });
  return {
    pageParams: data.pageParams,
    pages: transformedPages,
  };
}

export function deserializeContentItems(
  contentItems: IContentLibraryItem[],
): IDeserializedContentLibraryItem[] {
  return contentItems.map((item: IContentLibraryItem) => {
    const {
      addedToCollectionTimestamp,
      addedToLibraryTimestamp,
      createdTime,
      ...rest
    } = item;
    return {
      ...rest,
      ...(addedToCollectionTimestamp && {
        addedToCollectionTimestamp: new Date(addedToCollectionTimestamp),
      }),
      ...(addedToLibraryTimestamp && {
        addedToLibraryTimestamp: new Date(addedToLibraryTimestamp),
      }),
      createdTime: new Date(createdTime),
    };
  });
}

export function useCreatorsCampaignsGetSparkCodeMediaIdsQuery(
  req: GetSparkCodeMediaIdsRequest,
) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CreatorsCampaigns,
      QueryKeyConstants.CreatorsCampaignsSparkCodeData,
      req,
    ],
    queryFn: async () => {
      if (!isDefined(req.campaignSlugName)) {
        throw new Error(
          'Undefined campaignSlugName. Required to get media ids with spark code.',
        );
      }

      return getSparkCodeMediaIds(req);
    },
    enabled: isDefined(req.campaignSlugName),
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
}

export function useGetGASettingsStatusQuery() {
  const fetchCombinedData = async () => {
    const [metadata, trackingParams] = await Promise.all([
      getMetadata(),
      getCurrentTrackingParams(),
    ]);
    return { metadata, trackingParams };
  };

  return useQuery({
    queryKey: [QueryKeyConstants.Settings, QueryKeyConstants.SettingsGAStatus],
    queryFn: fetchCombinedData,
    staleTime: FIVE_MINUTES_STALE_TIME,
    select: (data) => {
      const { isEcomEnabled, isGAConnected } = hasCreatorCampaignsGAEnabled(
        data.metadata.ecommerceProvider,
        data.metadata.ecommerceTracking,
      );
      const hasReservedGAParam = (
        data.trackingParams.trackingParams?.paramGroups ?? []
      ).some(
        (paramGroup) =>
          paramGroup.id === 'google' &&
          paramGroup.inUse &&
          paramGroup.params.some((param) => param.curalateReserved),
      );

      return {
        isGAConnected,
        isEcomEnabled,
        hasReservedGAParam,
      };
    },
  });
}

// Impact.com Campaign Workflow Integration Query Hooks

/**
 * Query hook to search available Impact.com programs
 * @param req - Search parameters including pagination and filters
 */

/**
 * Mutation hook to link Impact.com program to campaign
 */
export const useLinkImpactProgramMutation = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req: LinkImpactProgramRequest) => {
      return linkImpactProgramToCampaign(req);
    },
    onError: (error) => {
      const errorMessage =
        getRequestErrorMessage(error) ??
        'An unexpected error occurred. Please try again or contact Bazaarvoice Support.';

      dispatch(
        toastNotificationsActions.addToastNotification({
          message: errorMessage,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
          title: 'Failed to Link Impact Program',
          type: ToastType.ERROR,
        }),
      );
    },
    onSettled: async (_data, _error, { campaignSlugName }) => {
      // Invalidate the linked program query to refresh the data
      await queryClient.invalidateQueries({
        queryKey: [
          QueryKeyConstants.Campaigns,
          QueryKeyConstants.CreatorsCampaigns,
          QueryKeyConstants.CreatorsCampaignWorkflow,
          campaignSlugName,
          QueryKeyConstants.CreatorsCampaignsImpact,
        ],
      });
    },
    onSuccess: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          message:
            'Impact.com program has been successfully linked to the campaign.',
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
          title: 'Impact Program Linked',
          type: ToastType.SUCCESS,
        }),
      );
    },
    retry: false,
  });
};

/**
 * Query hook to get the linked Impact.com program for a campaign
 */
export const useLinkedImpactProgramQuery = ({
  campaignSlugName,
  enabled,
}: {
  campaignSlugName: string | undefined;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CreatorsCampaigns,
      QueryKeyConstants.CreatorsCampaignWorkflow,
      campaignSlugName,
      QueryKeyConstants.CreatorsCampaignsImpact,
    ],
    queryFn: async ({ signal }) => {
      if (!isDefined(campaignSlugName)) {
        throw new Error('Undefined CampaignSlugName');
      }
      try {
        return await getLinkedImpactProgram({ campaignSlugName }, { signal });
      } catch (err) {
        const errorCode = getRequestErrorCode(err);
        // 404 means the program hasn't been linked yet, which is a valid state
        // Return null to indicate no program is currently linked
        if (errorCode === 404) {
          return null;
        }
        throw err;
      }
    },
    enabled: isDefined(campaignSlugName) && enabled,
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};

export const useMapCreatorsToImpactPartnersMutation = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (req: ImpactMappingRequest) => {
      return mapCreatorsToImpactPartners(req);
    },
    onError: (error) => {
      const errorMessage =
        getRequestErrorMessage(error) ??
        'Failed to map creators to Impact.com partners. Please try again.';

      dispatch(
        toastNotificationsActions.addToastNotification({
          message: errorMessage,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
          title: 'Failed to Map Impact Partners',
          type: ToastType.ERROR,
        }),
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [
          QueryKeyConstants.Campaigns,
          QueryKeyConstants.CreatorsCampaigns,
          QueryKeyConstants.CreatorsCampaignWorkflow,
          QueryKeyConstants.CreatorsCampaignsImpact,
        ],
      });
    },
    retry: false,
  });
};

/**
 * Mutation hook to unlink creator from Impact.com partner
 */
export const useUnlinkCreatorFromImpactPartnerMutation = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (req: UnlinkCreatorFromImpactPartnerRequest) => {
      return unlinkCreatorFromImpactPartner(req);
    },
    onError: (error) => {
      const errorMessage =
        getRequestErrorMessage(error) ??
        'Failed to unlink creator from Impact.com partner. Please try again.';

      dispatch(
        toastNotificationsActions.addToastNotification({
          message: errorMessage,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
          title: 'Failed to Unlink Impact Partner',
          type: ToastType.ERROR,
        }),
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [
          QueryKeyConstants.Campaigns,
          QueryKeyConstants.CreatorsCampaigns,
          QueryKeyConstants.CreatorsCampaignWorkflow,
          QueryKeyConstants.CreatorsCampaignsImpact,
        ],
      });
      dispatch(
        toastNotificationsActions.addToastNotification({
          message:
            'Creator has been successfully unlinked from Impact partner.',
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
          title: 'Impact Partner Unlinked',
          type: ToastType.SUCCESS,
        }),
      );
    },
    retry: false,
  });
};

/**
 * Mutation hook to set off the creator campaign sensitive links warning
 */
export const useDisableCreatorsCampaignSensitiveLinksWarningMutation = () => {
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: async () => {
      return disableCreatorsCampaignSensitiveLinksWarning();
    },
    onError: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          message:
            'Failed to disable sensitive links warning. Please try again.',
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
          title: "Couldn't disable sensitive links warning",
          type: ToastType.WARNING,
        }),
      );
    },
  });
};

/**
 * Query hook to get unmapped creators for Impact.com partner mapping
 */
export const usePartnerMappingsQuery = (
  req: GetPartnerMappingsRequest,
  {
    enabled = true,
    showToastOnError = true,
  }: { enabled?: boolean; showToastOnError?: boolean },
) => {
  const dispatch = useDispatch();
  const query = useQuery({
    enabled,
    queryFn: () => getPartnerMappings(req),
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CreatorsCampaigns,
      QueryKeyConstants.CreatorsCampaignWorkflow,
      QueryKeyConstants.CreatorsCampaignsImpact,
      QueryKeyConstants.CreatorsCampaignsImpactPartnerMappings,
      req,
    ],
    retry: false,
  });

  useEffect(() => {
    if (!showToastOnError || !query.error) {
      return;
    }

    const errorCode = getRequestErrorCode(query.error);

    // Only show toast for authentication/permission errors (401/403)
    if (errorCode === 401 || errorCode === 403) {
      const errorMessage =
        getRequestErrorMessage(query.error) ??
        'An unexpected error occurred. Please try again or contact Bazaarvoice Support.';

      dispatch(
        toastNotificationsActions.addToastNotification({
          message: errorMessage,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
          title: 'Failed to Get Partner Mappings',
          type: ToastType.ERROR,
        }),
      );
    }
  }, [query.error, showToastOnError, dispatch]);

  return query;
};

/**
 * Query hook to get connected Impact partners for a specific creator
 */
export const useConnectedImpactPartnersQuery = (
  req: GetConnectedImpactPartnersRequest,
  enabled?: boolean,
) => {
  return useQuery({
    enabled: enabled && isDefined(req.campaignSlugName),
    queryFn: async ({ signal }) => {
      return getConnectedImpactPartners(req, { signal });
    },
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CreatorsCampaigns,
      QueryKeyConstants.CreatorsCampaignWorkflow,
      QueryKeyConstants.CreatorsCampaignsImpact,
      req,
    ],
    retry: false,
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};

/**
 * Mutation hook to upload creator media to a campaign
 */
export function useUploadCreatorMediaMutation() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (req: UploadCreatorMediaRequest) => {
      return uploadCreatorMedia(req);
    },
    onSuccess: (response) => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Linking Complete',
          message:
            response.message || 'Media linked successfully to the campaign',
          type: ToastType.SUCCESS,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
      queryClient.invalidateQueries({
        queryKey: [
          QueryKeyConstants.Campaigns,
          QueryKeyConstants.CreatorsCampaigns,
          QueryKeyConstants.CreatorsCampaignsContents,
        ],
      });
    },
    onError: (error) => {
      const errorCode = getRequestErrorCode(error);
      const displayMessage =
        errorCode === 400
          ? 'Please check if username and media are correct'
          : 'An unexpected error occurred. Please try again or contact Bazaarvoice Support.';
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Linking Failed',
          message: displayMessage,
          type: ToastType.ERROR,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
  });
}
